import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatAmountMinor, resolveFinanceLocale } from '../finance-format';
import { Block } from './PrimaNotaBlocks';
import { StatementFormatsSection } from './PrimaNotaProduced';
import type { ProducedRegister } from './prima-nota-model';

const CONTROL_TONES: Record<string, 'success' | 'error' | 'warning'> = {
  failed: 'error',
  'not-run': 'warning',
};

/** A control's state as a semantic tone, with the meaning the page always gave it: only a failed
 * control reads as an error and only one not run as a warning; anything else reads as passed. */
function controlTone(state: string): 'success' | 'error' | 'warning' {
  return CONTROL_TONES[state] ?? 'success';
}

type OpenItemsSectionProps = {
  register: ProducedRegister;
  treatments: Record<string, string>;
  busy: boolean;
  locked: boolean;
  onTreatmentChange: (text: string, treatment: string) => void;
  onRegenerate: () => void;
};

function OpenItemRow({
  item,
  index,
  treatments,
  onTreatmentChange,
}: {
  item: ProducedRegister['receipt']['openItems'][number];
  index: number;
  treatments: Record<string, string>;
  onTreatmentChange: (text: string, treatment: string) => void;
}) {
  const { t, i18n } = useTranslation('finance');
  return (
    <li
      className="bg-background border-border rounded-md border p-3"
      data-testid={`open-item-${index}`}
    >
      <p className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
        <span>
          <span className="font-medium">{item.date ?? '—'}</span>
          <span className="text-muted-foreground"> · {item.accountKey}</span>
        </span>
        <span className="font-medium">
          {formatAmountMinor(item.amountMinor, resolveFinanceLocale(i18n.resolvedLanguage))}
        </span>
      </p>
      <p className="text-muted-foreground mt-0.5 text-xs break-words">{item.text}</p>
      {item.treatment === null ? (
        <div className="mt-2 space-y-2">
          <label className="field-label" htmlFor={`open-item-input-${index}`}>
            {t('primaNota.openItems.treatmentLabel')}
          </label>
          <Input
            id={`open-item-input-${index}`}
            value={treatments[item.text] ?? ''}
            onChange={(event) => onTreatmentChange(item.text, event.target.value)}
          />
        </div>
      ) : (
        <p className="text-success mt-1 text-xs">{item.treatment}</p>
      )}
    </li>
  );
}

function OpenItemsSection(props: OpenItemsSectionProps) {
  const { register, treatments, busy, locked } = props;
  const { t } = useTranslation('finance');
  const unclassified = register.receipt.openItems.filter((item) => item.treatment === null);
  return (
    <div className="space-y-3" data-testid="prima-nota-open-items">
      <div>
        <h4 className="text-sm font-semibold">
          {t('primaNota.openItems.heading', { count: register.receipt.openItems.length })}
        </h4>
        <p className="text-muted-foreground mt-0.5 text-[13px] leading-5">
          {unclassified.length === 0
            ? t('primaNota.openItems.allClassified')
            : t('primaNota.openItems.hint', { count: unclassified.length })}
        </p>
      </div>
      <ul className="space-y-3">
        {register.receipt.openItems.map((item, index) => (
          <OpenItemRow
            key={`${item.accountKey}:${item.date ?? ''}:${item.amountMinor}:${index}`}
            item={item}
            index={index}
            treatments={treatments}
            onTreatmentChange={props.onTreatmentChange}
          />
        ))}
      </ul>
      {unclassified.length > 0 && (
        <Button
          variant="outline"
          disabled={locked}
          data-testid="prima-nota-regenerate"
          onClick={props.onRegenerate}
        >
          {busy ? t('primaNota.generate.running') : t('primaNota.openItems.regenerate')}
        </Button>
      )}
    </div>
  );
}

function ControlsSection({ register }: { register: ProducedRegister }) {
  const { t } = useTranslation('finance');
  return (
    <div className="space-y-2">
      <h4 className="card-label">{t('primaNota.generate.controls')}</h4>
      <ul className="space-y-2 text-sm">
        {register.receipt.controls.map((control) => (
          <li key={control.control} className="flex flex-wrap items-baseline gap-2">
            <span className={`status-badge ${controlTone(control.state)}`}>
              {t(`primaNota.generate.controlState.${control.state}`, control.state)}
            </span>
            <span>
              {control.control}
              {control.detail !== undefined && control.detail !== '' ? ` (${control.detail})` : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AnomaliesSection({ register }: { register: ProducedRegister }) {
  const { t } = useTranslation('finance');
  return (
    <div className="space-y-2">
      <h4 className="card-label">{t('primaNota.generate.anomalies')}</h4>
      {register.receipt.anomalies.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('primaNota.generate.noAnomalies')}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {register.receipt.anomalies.map((anomaly, index) => (
            <li key={`${anomaly.documentReference}:${anomaly.code}:${index}`}>
              <span className="font-medium">{anomaly.documentReference}</span> — {anomaly.detail}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RejectedRowsSection({ register }: { register: ProducedRegister }) {
  const { t } = useTranslation('finance');
  return (
    <div className="space-y-2">
      <h4 className="card-label">{t('primaNota.generate.rejected')}</h4>
      {register.receipt.rejectedRows.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('primaNota.generate.noRejected')}</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {register.receipt.rejectedRows.map((row, index) => (
            <li key={`${row.source}:${row.row}:${index}`}>
              {row.source} · {row.row} — {row.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ProducedFindings(props: OpenItemsSectionProps) {
  const { register } = props;
  const { t } = useTranslation('finance');
  return (
    <Block
      id="prima-nota-findings"
      focusable
      title={t('primaNota.result.heading')}
      testId="prima-nota-findings"
    >
      {/*
        THE STEP THAT WAS STRUCTURALLY IMPOSSIBLE BEFORE 2026-09-21.

        The server has always accepted a treatment per unsettled movement, and the screen has
        always sent none — so every prima nota produced here printed "da classificare" against
        every open item, which is not a document an accountant can file. The reason was not an
        oversight in the form: the items exist only inside the produced workbook's annex sheet, so
        the screen had nothing to ask ABOUT until the receipt began carrying them.

        Hence the order, which reads backwards until you see why: produce first, classify second,
        produce again. The operator cannot judge what they have not seen, and a first run is the
        only thing that shows it. The prima nota stays downloadable throughout, but it is not
        vouchable while anything here is blank, and the server decides that, not this panel.
      */}
      {register.receipt.openItems.length > 0 && <OpenItemsSection {...props} />}

      <StatementFormatsSection
        checks={register.receipt.statementFormats ?? []}
        labelOf={(accountKey) =>
          register.readiness.statements.find((statement) => statement.accountKey === accountKey)
            ?.accountLabel ?? accountKey
        }
      />

      <ControlsSection register={register} />
      <AnomaliesSection register={register} />
      <RejectedRowsSection register={register} />
    </Block>
  );
}
